// src/app/_service/note-size.service.ts
// A note's size as shown everywhere in the app (Home "Size" column and sort, View/Edit "Note size",
// export "as current view" order): the note record itself (Note.Size — computed by the server, or
// locally for an unsynced save) PLUS the size of all its attachments.
//
// Attachment sizes come from the local attachments store, which the sync keeps filled with every
// note's attachments (and which also holds queued offline uploads), so the total is available
// offline and doesn't need the note document to change whenever an attachment is added or removed.

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { Note } from './data.service';
import { DataOfflineService } from './data-offline.service';

@Injectable({ providedIn: 'root' })
export class NoteSizeService {
  private attachmentBytesByNote = new Map<string, number>();
  private readonly changedSubject = new BehaviorSubject<void>(undefined);

  /** Emits whenever attachment sizes were recounted (sizes shown may have changed). */
  readonly changed$: Observable<void> = this.changedSubject.asObservable();

  constructor(private readonly offline: DataOfflineService) {
    void this.refresh();
    this.offline
      .onAttachmentsChanged()
      .pipe(debounceTime(250))
      .subscribe(() => void this.refresh());
  }

  /** Bytes of all of the note's (locally known) attachments. */
  attachmentBytes(noteId: string | null | undefined): number {
    return noteId ? this.attachmentBytesByNote.get(noteId) ?? 0 : 0;
  }

  /** Note record + attachments, in bytes; undefined when neither is known. */
  totalSizeBytes(note: Partial<Note> | null | undefined): number | undefined {
    if (!note) return undefined;
    const recordSize = Number(note.Size);
    const hasRecordSize = note.Size != null && Number.isFinite(recordSize) && recordSize >= 0;
    const attachments = this.attachmentBytes(note._id);
    if (!hasRecordSize && attachments === 0) return undefined;
    return (hasRecordSize ? recordSize : 0) + attachments;
  }

  /** Same as totalSizeBytes, 0 when unknown — for sorting. */
  sortableSize(note: Partial<Note> | null | undefined): number {
    return this.totalSizeBytes(note) ?? 0;
  }

  async refresh(): Promise<void> {
    try {
      this.attachmentBytesByNote = await this.offline.getAttachmentBytesByNote();
      this.changedSubject.next();
    } catch {
      // IndexedDB unavailable — keep the last counts (record sizes still show).
    }
  }
}
